import { z } from 'zod'
import { COVER_LETTER_LIMITS, UPLOAD_LIMITS } from '../data/careers'

/** Type and size check before any upload starts. Returns an error message or null. */
export function validateUploadFile(file: File): string | null {
  const name = file.name.toLowerCase()
  const extensionOk = UPLOAD_LIMITS.extensions.some((ext) => name.endsWith(ext))
  const typeOk = !file.type || (UPLOAD_LIMITS.mimeTypes as readonly string[]).includes(file.type)
  if (!extensionOk || !typeOk) return 'Please upload a PDF or Word (.docx) file.'
  if (file.size > UPLOAD_LIMITS.maxBytes) return 'This file is over 5 MB. Please upload a smaller version.'
  if (file.size === 0) return 'This file appears to be empty. Please choose another.'
  return null
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * The one application schema. The server route must parse with this same
 * schema: client validation is a courtesy to the applicant, not a control.
 */

/** Tomorrow or later, compared as calendar dates so the time of day never matters. */
function isFutureDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const today = new Date()
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`
  return value > todayIso
}

/** Accepts linkedin.com with or without the scheme and www. */
export function normaliseLinkedIn(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

export function normaliseUrl(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

const optionalText = (max: number, message: string) =>
  z.string().trim().max(max, message).optional().or(z.literal(''))

export const applicationSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, 'Please enter your first name.')
      .max(100, 'Please keep this under 100 characters.'),
    lastName: z
      .string()
      .trim()
      .min(1, 'Please enter your last name.')
      .max(100, 'Please keep this under 100 characters.'),
    email: z.string().trim().email('Please enter a valid email address.'),
    phone: z
      .string()
      .trim()
      .refine(
        (value) => {
          if (!value) return true
          if (!/^\+?[0-9\s()-]+$/.test(value)) return false
          const digits = value.replace(/\D/g, '').length
          return digits >= 7 && digits <= 15
        },
        { message: 'Please include your country code, for example +250 788 123 456.' },
      )
      .optional()
      .or(z.literal('')),
    country: z.string().min(1, 'Please choose your country of residence.'),
    city: z
      .string()
      .trim()
      .min(1, 'Please enter your city.')
      .max(100, 'Please keep this under 100 characters.'),
    linkedinUrl: z
      .string()
      .trim()
      .refine(
        (value) => !value || /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/.+/i.test(value),
        { message: 'Please enter a LinkedIn profile address, starting linkedin.com.' },
      )
      .optional()
      .or(z.literal('')),
    portfolioUrl: z
      .string()
      .trim()
      .refine(
        (value) => {
          if (!value) return true
          try {
            const url = new URL(normaliseUrl(value))
            return url.hostname.includes('.')
          } catch {
            return false
          }
        },
        { message: 'Please enter a valid web address.' },
      )
      .optional()
      .or(z.literal('')),
    positionSlug: z.string().min(1, 'Please choose the position you are applying for.'),
    preferredStart: z
      .string()
      .min(1, 'Please choose a preferred start date.')
      .refine(isFutureDate, { message: 'Start date must be in the future.' }),
    availability: z.enum(['full-time', 'part-time', 'flexible'], {
      errorMap: () => ({ message: 'Please choose your availability.' }),
    }),
    referralSource: z.string().optional().or(z.literal('')),
    referralSourceOther: optionalText(200, 'Please keep this under 200 characters.'),
    cvS3Key: z.string().min(1, 'Please attach your CV.'),
    cvFilename: z.string().min(1, 'Please attach your CV.'),
    coverLetterMode: z.enum(['upload', 'write'], {
      errorMap: () => ({ message: 'Please choose how you will provide a cover letter.' }),
    }),
    coverLetterS3Key: z.string().optional(),
    coverLetterFilename: z.string().optional(),
    coverLetterText: z.string().optional(),
    additionalInfo: optionalText(1000, 'Please keep this under 1,000 characters.'),
    consentGiven: z.literal(true, {
      errorMap: () => ({ message: 'Please agree to the processing of your data so we can consider your application.' }),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.coverLetterMode === 'upload' && !data.coverLetterS3Key) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['coverLetterFile'],
        message: 'Please attach your cover letter.',
      })
    }
    if (data.coverLetterMode === 'write') {
      const length = (data.coverLetterText ?? '').trim().length
      if (length < COVER_LETTER_LIMITS.min) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['coverLetterText'],
          message: `Please write at least ${COVER_LETTER_LIMITS.min} characters. You have ${length}.`,
        })
      } else if (length > COVER_LETTER_LIMITS.max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['coverLetterText'],
          message: `Please keep your letter under ${COVER_LETTER_LIMITS.max} characters.`,
        })
      }
    }
    if (data.referralSource === 'Other' && !(data.referralSourceOther ?? '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['referralSourceOther'],
        message: 'Please tell us where you heard about us.',
      })
    }
  })

export type ApplicationInput = z.infer<typeof applicationSchema>

/** Field key to error message, first issue per field. */
export type FieldErrors = Partial<Record<string, string>>

export function collectErrors(error: z.ZodError): FieldErrors {
  const errors: FieldErrors = {}
  error.issues.forEach((issue) => {
    const key = String(issue.path[0] ?? 'form')
    if (!errors[key]) errors[key] = issue.message
  })
  return errors
}
